import React, { Fragment, useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import BarcodeReader from 'react-barcode-reader';
import { useDispatch, useSelector } from 'react-redux';
import { useRouteMatch } from 'react-router-dom';
import { get, isEmpty } from 'lodash/fp';
import { toastr } from 'react-redux-toastr';
import {
  getItemSku,
  getIsFetching as isFetchingItemSku,
  getIsFetched as isFetchedItemSku,
  getShowAddItemSkuModal
} from '../../../redux/Inventory/selectors/components/item-sku-lookup';
import { getUserPermissionsAsMap } from '../../../redux/selectors/permission';
import {
  clearItemSku,
  hideAddItemSkuModal
} from '../../../redux/Inventory/actions/components/item-sku-lookup';
import { fetchInventorySkuBySku } from '../../../redux/Inventory/sagas/components/item-sku-lookup/routines';
import {
  inventoryViewItemPath,
  inventoryViewPackagePath
} from '../../../routes/Paths';
import {
  ItemType,
  PackageType
} from '../../../constants/inventory/InventoryTypes';
import * as PERMISSIONS from '../../../constants/permissions/inventory';
import { hasViewAccess, hasModifyAccess } from '../../../helpers/permission';
import { parseInt } from '../../../helpers/utils';
import pagePathToUrl from '../../../helpers/pagePathToUrl';
import history from '../../../routes/history';
import InventoryAddSkuModal from '../../../containers/inventory/AddSkuModal';
import ModalLoader from './ModalLoader';

const ViewPathByItemType = {
  [ItemType]: inventoryViewItemPath,
  [PackageType]: inventoryViewPackagePath
};

const InventoryItemSkuLookup = () => {
  const dispatch = useDispatch();
  const itemSku = useSelector(getItemSku);
  const isFetching = useSelector(isFetchingItemSku);
  const isFetched = useSelector(isFetchedItemSku);
  const permissions = useSelector(getUserPermissionsAsMap);
  const showAddItemSkuModal = useSelector(getShowAddItemSkuModal);
  const { params, path, url: currentUrl } = useRouteMatch();
  const isItemViewPath =
    path === inventoryViewItemPath || path === inventoryViewPackagePath;
  const selectedItemId = isItemViewPath ? parseInt(get('id', params)) : null;
  const [scannedBarcode, setScannedBarcode] = useState('');

  const itemViewUrl = useMemo(
    () => {
      if (isEmpty(itemSku) || !get('id')) return '';
      const { itemId: id, itemType } = itemSku;
      return pagePathToUrl(ViewPathByItemType[itemType], { id });
    },
    [itemSku]
  );

  useEffect(
    () => {
      if (
        isFetched &&
        !isEmpty(permissions) &&
        !hasModifyAccess(PERMISSIONS.SKU, permissions)
      ) {
        toastr.error(
          'Access Denied',
          'Sorry, you do not have enough permission.'
        );
      }
    },
    [isFetched, permissions]
  );

  useEffect(
    () => {
      if (!itemViewUrl) return;

      if (currentUrl !== itemViewUrl) {
        history.push(itemViewUrl);
      } else {
        setScannedBarcode('');
        dispatch(clearItemSku());
      }
    },
    [currentUrl, itemViewUrl]
  );

  const handleHideAddSkuModal = () => {
    setScannedBarcode('');
    dispatch(hideAddItemSkuModal());
  };

  const handleScan = barcode => {
    const hasViewSkuAccess = hasViewAccess(PERMISSIONS.SKU, permissions);
    const hasItemViewAccess = hasViewAccess(PERMISSIONS.ITEM, permissions);
    if (!hasViewSkuAccess || !hasItemViewAccess) {
      toastr.error(
        'Access Denied',
        'Sorry, you do not have enough permission.'
      );
    }

    if (get('sku', itemSku) !== barcode) {
      setScannedBarcode(barcode);
      dispatch(fetchInventorySkuBySku(barcode));
    }
  };

  return (
    <Fragment>
      {isFetching && <ModalLoader />}
      {!isFetching &&
        hasModifyAccess(PERMISSIONS.SKU, permissions) && (
          <InventoryAddSkuModal
            isSaving={false}
            show={showAddItemSkuModal}
            itemId={selectedItemId}
            sku={scannedBarcode}
            onHide={handleHideAddSkuModal}
          />
        )}
      <BarcodeReader onScan={handleScan} />
    </Fragment>
  );
};

InventoryItemSkuLookup.propTypes = {
  permissions: PropTypes.shape({})
};

InventoryItemSkuLookup.defaultProps = {
  permissions: null
};

export default InventoryItemSkuLookup;
